// Comprehensive cooking terms database
// Categories: cutting, technique, temperature, preparation, equipment

export const COOKING_TERMS = {
  // Cutting techniques
  dice: {
    term: "Dice",
    definition: "To cut food into small, uniform cube-shaped pieces, typically 1/4 to 1/2 inch. There are three sizes: small dice (1/4 inch), medium dice (1/2 inch), and large dice (3/4 inch).",
    category: "cutting",
    icon: "🔪",
  },
  mince: {
    term: "Mince",
    definition: "To cut food into very small, fine pieces, smaller than diced. Commonly used for garlic, ginger, and herbs to release maximum flavor.",
    category: "cutting",
    icon: "🔪",
  },
  chop: {
    term: "Chop",
    definition: "To cut food into irregular pieces. Less precise than dicing, chopping creates varied sizes that are fine for soups, stews, and rustic dishes.",
    category: "cutting",
    icon: "🔪",
  },
  julienne: {
    term: "Julienne",
    definition: "To cut food into thin, uniform matchstick-shaped strips, typically 1/8 inch thick and 2-3 inches long. Also called 'matchstick cut.'",
    category: "cutting",
    icon: "🔪",
  },
  brunoise: {
    term: "Brunoise",
    definition: "To cut food into very small cubes (1/8 inch). Start with julienne strips, then cut across to create tiny cubes. Often used for garnishes.",
    category: "cutting",
    icon: "🔪",
  },
  chiffonade: {
    term: "Chiffonade",
    definition: "To slice herbs or leafy greens into thin ribbons. Stack leaves, roll tightly, then slice across the roll to create delicate strips.",
    category: "cutting",
    icon: "🔪",
  },
  slice: {
    term: "Slice",
    definition: "To cut food into thin, flat pieces using a smooth cutting motion. Thickness varies based on the recipe requirements.",
    category: "cutting",
    icon: "🔪",
  },
  cube: {
    term: "Cube",
    definition: "To cut food into larger cube shapes, typically 1/2 inch or larger. Often used for meat, potatoes, and bread.",
    category: "cutting",
    icon: "🔪",
  },

  // Cooking techniques
  blanch: {
    term: "Blanch",
    definition: "To briefly cook food in boiling water, then immediately transfer to ice water to stop cooking. Used to brighten colors, soften textures, or loosen skins for peeling.",
    category: "technique",
    icon: "🍳",
  },
  braise: {
    term: "Braise",
    definition: "To cook food slowly in a small amount of liquid in a covered pot. Combines both dry and wet heat - first searing, then slow cooking. Perfect for tough cuts of meat.",
    category: "technique",
    icon: "🍳",
  },
  saute: {
    term: "Saute",
    definition: "To cook food quickly in a small amount of fat over high heat while stirring or tossing. The French word means 'to jump' - food should move in the pan.",
    category: "technique",
    icon: "🍳",
  },
  sautee: {
    term: "Saute",
    definition: "To cook food quickly in a small amount of fat over high heat while stirring or tossing. The French word means 'to jump' - food should move in the pan.",
    category: "technique",
    icon: "🍳",
  },
  sear: {
    term: "Sear",
    definition: "To brown the surface of food quickly at high temperature. Creates a flavorful crust through the Maillard reaction. Don't move the food until it releases naturally.",
    category: "technique",
    icon: "🍳",
  },
  deglaze: {
    term: "Deglaze",
    definition: "To add liquid (wine, broth, or water) to a hot pan to loosen and dissolve the caramelized bits (fond) stuck to the bottom, creating a flavorful sauce base.",
    category: "technique",
    icon: "🍳",
  },
  fold: {
    term: "Fold",
    definition: "To gently combine a light mixture into a heavier one without deflating. Use a spatula to cut down through the center, sweep along the bottom, and fold over the top.",
    category: "technique",
    icon: "🍳",
  },
  cream: {
    term: "Cream",
    definition: "To beat butter and sugar together until light and fluffy, incorporating air. The mixture should be pale in color and have increased volume.",
    category: "technique",
    icon: "🍳",
  },
  baste: {
    term: "Baste",
    definition: "To pour pan juices, melted fat, or liquid over food during cooking to keep it moist and add flavor. Use a spoon or baster for even coating.",
    category: "technique",
    icon: "🍳",
  },
  whisk: {
    term: "Whisk",
    definition: "To beat ingredients rapidly with a whisk or fork to incorporate air and blend thoroughly. Used for eggs, sauces, and batters.",
    category: "technique",
    icon: "🍳",
  },
  reduce: {
    term: "Reduce",
    definition: "To boil a liquid to evaporate water, concentrating flavors and thickening consistency. Usually done uncovered to allow evaporation.",
    category: "technique",
    icon: "🍳",
  },
  caramelize: {
    term: "Caramelize",
    definition: "To heat sugar until it liquefies and turns golden brown, or to cook foods with natural sugars (like onions) until their sugars brown and develop deep flavor.",
    category: "technique",
    icon: "🍳",
  },
  roast: {
    term: "Roast",
    definition: "To cook food in an oven with dry heat, usually uncovered. Higher temperatures create browning on the outside while keeping the inside moist.",
    category: "technique",
    icon: "🍳",
  },
  broil: {
    term: "Broil",
    definition: "To cook food directly under high heat from above. Similar to grilling but upside-down. Keep food close to the heat source and watch carefully to prevent burning.",
    category: "technique",
    icon: "🍳",
  },
  grill: {
    term: "Grill",
    definition: "To cook food on a grate over direct heat from below, typically from gas, charcoal, or wood fire. Creates distinctive char marks and smoky flavor.",
    category: "technique",
    icon: "🍳",
  },
  steam: {
    term: "Steam",
    definition: "To cook food by surrounding it with steam from boiling water. The food doesn't touch the water directly. Preserves nutrients and keeps food moist.",
    category: "technique",
    icon: "🍳",
  },
  poach: {
    term: "Poach",
    definition: "To cook food gently in liquid just below boiling point (160-180F). The liquid should show slight movement but no bubbles. Perfect for delicate foods like eggs and fish.",
    category: "technique",
    icon: "🍳",
  },
  flambe: {
    term: "Flambe",
    definition: "To ignite alcohol in a pan to burn off the alcohol while adding flavor. Tilt pan away from you when lighting. The flame will extinguish when alcohol burns off.",
    category: "technique",
    icon: "🍳",
  },
  emulsify: {
    term: "Emulsify",
    definition: "To combine two liquids that don't naturally mix (like oil and vinegar) into a stable, uniform mixture. Requires vigorous whisking or blending.",
    category: "technique",
    icon: "🍳",
  },
  knead: {
    term: "Knead",
    definition: "To work dough with your hands by pressing, folding, and stretching. Develops gluten for bread structure. Continue until dough is smooth and elastic.",
    category: "technique",
    icon: "🍳",
  },
  proof: {
    term: "Proof",
    definition: "To let yeast dough rise in a warm place before baking. The yeast produces gas that expands the dough. Usually takes 1-2 hours until doubled in size.",
    category: "technique",
    icon: "🍳",
  },
  temper: {
    term: "Temper",
    definition: "To slowly raise the temperature of a cold ingredient by gradually adding hot liquid. Prevents curdling when combining eggs with hot mixtures.",
    category: "technique",
    icon: "🍳",
  },
  zest: {
    term: "Zest",
    definition: "To remove the colorful outer layer of citrus fruit skin, which contains flavorful oils. Avoid the white pith underneath, which is bitter.",
    category: "technique",
    icon: "🍳",
  },
  score: {
    term: "Score",
    definition: "To make shallow cuts in the surface of food, usually in a crosshatch pattern. Helps marinades penetrate, fat render, and bread expand during baking.",
    category: "technique",
    icon: "🍳",
  },
  truss: {
    term: "Truss",
    definition: "To tie poultry with kitchen string so it cooks evenly and maintains its shape. Tuck wings under and tie legs together.",
    category: "technique",
    icon: "🍳",
  },

  // Temperature terms
  simmer: {
    term: "Simmer",
    definition: "To cook liquid at a temperature just below boiling (185-200F). Small bubbles should gently break the surface. More gentle than boiling, perfect for sauces and soups.",
    category: "temperature",
    icon: "🌡️",
  },
  boil: {
    term: "Boil",
    definition: "To heat liquid until it reaches 212F at sea level with rapid, rolling bubbles breaking the surface continuously. Used for pasta, vegetables, and reducing liquids.",
    category: "temperature",
    icon: "🌡️",
  },
  "al dente": {
    term: "Al Dente",
    definition: "Italian for 'to the tooth.' Pasta or vegetables cooked until tender but still firm when bitten. Should have slight resistance but no hard, uncooked center.",
    category: "temperature",
    icon: "🌡️",
  },
  "room temperature": {
    term: "Room Temperature",
    definition: "Approximately 68-72F. Ingredients like butter and eggs often need to be at room temperature for proper mixing and incorporation in baking.",
    category: "temperature",
    icon: "🌡️",
  },

  // Preparation terms
  marinate: {
    term: "Marinate",
    definition: "To soak food in a seasoned liquid (marinade) to add flavor and tenderize. Marinades typically contain acid, oil, and seasonings. Time varies from 30 minutes to 24 hours.",
    category: "preparation",
    icon: "🥄",
  },
  brine: {
    term: "Brine",
    definition: "To soak food in salted water, sometimes with sugar and spices. Improves moisture retention and seasoning penetration, especially for poultry and pork.",
    category: "preparation",
    icon: "🥄",
  },
  cure: {
    term: "Cure",
    definition: "To preserve food using salt, sugar, and sometimes nitrates. Draws out moisture and prevents bacterial growth. Used for bacon, ham, and gravlax.",
    category: "preparation",
    icon: "🥄",
  },
  rest: {
    term: "Rest",
    definition: "To let cooked meat sit before cutting, allowing juices to redistribute throughout. Usually 5-15 minutes depending on size. Prevents juices from running out when sliced.",
    category: "preparation",
    icon: "🥄",
  },
  season: {
    term: "Season",
    definition: "To add salt, pepper, and other spices to enhance flavor. Season in stages during cooking, not just at the end, for more developed flavor.",
    category: "preparation",
    icon: "🥄",
  },
  dredge: {
    term: "Dredge",
    definition: "To coat food lightly in flour, breadcrumbs, or other dry ingredients before cooking. Creates a crispy exterior when fried or sauteed.",
    category: "preparation",
    icon: "🥄",
  },
  bread: {
    term: "Bread",
    definition: "To coat food in a three-step process: flour, beaten egg, then breadcrumbs. Creates a crispy coating for frying. Pat each layer on gently.",
    category: "preparation",
    icon: "🥄",
  },
  batter: {
    term: "Batter",
    definition: "A mixture of flour, liquid, and often eggs used to coat foods before frying or as a base for baked goods. Should coat a spoon but drip off slowly.",
    category: "preparation",
    icon: "🥄",
  },
  butterfly: {
    term: "Butterfly",
    definition: "To cut food nearly in half horizontally, leaving one edge attached, then opening it like a book. Used to flatten thick cuts for faster, more even cooking.",
    category: "preparation",
    icon: "🥄",
  },
  pound: {
    term: "Pound",
    definition: "To flatten meat using a mallet or heavy pan. Creates even thickness for uniform cooking. Place meat between plastic wrap to prevent tearing.",
    category: "preparation",
    icon: "🥄",
  },
  strain: {
    term: "Strain",
    definition: "To separate solids from liquids by pouring through a sieve or strainer. Use cheesecloth for finer straining. Press gently to extract more liquid.",
    category: "preparation",
    icon: "🥄",
  },
  puree: {
    term: "Puree",
    definition: "To blend food until completely smooth with no chunks. Use a blender, food processor, or immersion blender. Add liquid if needed for consistency.",
    category: "preparation",
    icon: "🥄",
  },
  mash: {
    term: "Mash",
    definition: "To crush food into a soft, lumpy texture using a fork, masher, or ricer. Common for potatoes, avocados, and bananas.",
    category: "preparation",
    icon: "🥄",
  },
  shred: {
    term: "Shred",
    definition: "To cut or tear food into thin strips or pieces. Use a grater for cheese, or two forks to pull apart cooked meat.",
    category: "preparation",
    icon: "🥄",
  },
  grate: {
    term: "Grate",
    definition: "To rub food against a grater to create small pieces or shreds. Different hole sizes create fine, medium, or coarse textures.",
    category: "preparation",
    icon: "🥄",
  },
  garnish: {
    term: "Garnish",
    definition: "To decorate a finished dish with edible items that complement the flavors. Should enhance both appearance and taste. Common garnishes include herbs, citrus, and sauces.",
    category: "preparation",
    icon: "🥄",
  },
  toss: {
    term: "Toss",
    definition: "To gently mix ingredients by lifting and turning, often used for salads. Ensures even coating of dressing without bruising delicate ingredients.",
    category: "preparation",
    icon: "🥄",
  },
  drizzle: {
    term: "Drizzle",
    definition: "To pour a thin stream of liquid over food in a slow, controlled manner. Often used for olive oil, sauces, or chocolate as a finishing touch.",
    category: "preparation",
    icon: "🥄",
  },
  dollop: {
    term: "Dollop",
    definition: "To place a spoonful of soft food (like cream, yogurt, or sauce) on top of a dish. Should be rounded and casual in appearance.",
    category: "preparation",
    icon: "🥄",
  },

  // Additional cutting terms
  fillet: {
    term: "Fillet",
    definition: "To remove bones from meat or fish, creating a boneless piece. Requires a sharp, flexible knife and careful technique.",
    category: "cutting",
    icon: "🔪",
  },

  // Additional cooking techniques
  render: {
    term: "Render",
    definition: "To melt fat from meat slowly over low heat. The fat liquefies and can be used for cooking. Common with bacon, duck, and pork belly.",
    category: "technique",
    icon: "🍳",
  },
  sweat: {
    term: "Sweat",
    definition: "To cook vegetables in a small amount of fat over low heat until softened but not browned. Releases moisture and develops flavor without caramelization.",
    category: "technique",
    icon: "🍳",
  },
  "stir-fry": {
    term: "Stir-Fry",
    definition: "To cook small pieces of food quickly over very high heat while constantly stirring. Uses a wok or large skillet with a small amount of oil.",
    category: "technique",
    icon: "🍳",
  },
  "deep-fry": {
    term: "Deep-Fry",
    definition: "To cook food by submerging it completely in hot oil (350-375°F). Creates a crispy exterior while cooking the interior. Requires enough oil to cover the food.",
    category: "technique",
    icon: "🍳",
  },
  "pan-fry": {
    term: "Pan-Fry",
    definition: "To cook food in a moderate amount of fat in a skillet over medium to medium-high heat. Less oil than deep-frying, food is flipped partway through.",
    category: "technique",
    icon: "🍳",
  },
  shock: {
    term: "Shock",
    definition: "To plunge hot food into ice water immediately after cooking to stop the cooking process. Often used after blanching vegetables to preserve color and texture.",
    category: "technique",
    icon: "🍳",
  },
  parboil: {
    term: "Parboil",
    definition: "To partially cook food in boiling water before finishing with another cooking method. Often used for potatoes before roasting or dense vegetables before stir-frying.",
    category: "technique",
    icon: "🍳",
  },
  scald: {
    term: "Scald",
    definition: "To heat liquid (usually milk) to just below boiling point, when small bubbles form around the edges. Used to dissolve sugar, infuse flavors, or kill bacteria.",
    category: "technique",
    icon: "🍳",
  },
  glaze: {
    term: "Glaze",
    definition: "To coat food with a glossy mixture (sweet or savory) by brushing or dipping. Creates a shiny finish and adds flavor. Common for hams, pastries, and vegetables.",
    category: "technique",
    icon: "🍳",
  },
  bard: {
    term: "Bard",
    definition: "To wrap lean meat or poultry with strips of fat (like bacon) before cooking to add moisture and flavor. The fat bastes the meat as it renders.",
    category: "technique",
    icon: "🍳",
  },

  // Additional preparation terms
  macerate: {
    term: "Macerate",
    definition: "To soften fruit by soaking in sugar, alcohol, or other liquid. The fruit releases its juices and absorbs the flavors. Similar to marinating but for fruit.",
    category: "preparation",
    icon: "🥄",
  },
  roux: {
    term: "Roux",
    definition: "A cooked mixture of equal parts fat (usually butter) and flour used to thicken sauces. Cook longer for darker color and nuttier flavor.",
    category: "preparation",
    icon: "🥄",
  },
  infuse: {
    term: "Infuse",
    definition: "To steep ingredients in liquid to extract their flavors. Common for herbs in oil, vanilla in cream, or tea leaves in water.",
    category: "preparation",
    icon: "🥄",
  },
  bloom: {
    term: "Bloom",
    definition: "To hydrate gelatin in cold liquid before heating, or to toast spices in oil to release their essential oils and enhance flavor.",
    category: "preparation",
    icon: "🥄",
  },
};

// Get all term keys for regex matching
export const TERM_KEYS = Object.keys(COOKING_TERMS);

// Create regex pattern for term detection
export const TERM_PATTERN = new RegExp(
  `\\b(${TERM_KEYS.join('|')})\\b`,
  'gi'
);
